import { Injector, Pipe, PipeTransform } from '@angular/core';
import { CurrencyModel } from '../models/currency.model';
import { CurrencyService } from '../../currency/services/currency.service';

const PADDING = '000000';

@Pipe({name: 'chassisCurrency'})
export class CurrencyPipe implements PipeTransform {
    private defaultCurrency: CurrencyModel;
    private targetCurrency: CurrencyModel;

    constructor(protected injector: Injector) {
        this.defaultCurrency = this.injector.get(CurrencyService).getDefaultCurrency();
    }

    transform(value: number | string, toCurrency: string | CurrencyModel = null, formatOnly = false, includeSymbol = true, fractionSize: number = 2): string {
        if (toCurrency === null || typeof toCurrency === 'string') {
            toCurrency = this.getCurrency(toCurrency || this.defaultCurrency.code);
        }

        if (!formatOnly) {
            value = this.convert(value, toCurrency);
        }

        // remove unnecessary characters
        value          = (value + '').replace(/[\s\',!]/, '');
        const negative = Number(value) < 0;

        if (negative) {
            value = Number(value) * -1;
        }

        value = this.number_format(value, toCurrency['settings'].decimals, toCurrency['settings'].decimal, toCurrency['settings'].thousand);

        if (includeSymbol) {
            value = toCurrency.format.replace(/([0-9].*|)[0-9]/, (value + ''));
            // add a space after the symbol
            value = value.replace(toCurrency.symbol, toCurrency.symbol + ' ');
        }

        return value;
    }

    public getDefaultCurrency() {
        return this.defaultCurrency;
    }

    /**
     * Get a currency model by the given code
     * -> also provides settings like decimal and thousand separator
     *
     * @param currencyCode
     * @returns {any}
     */
    public getCurrency(currencyCode = null): CurrencyModel {
        if (currencyCode === null) {
            currencyCode = this.defaultCurrency.code;
        }

        const currencies = this.injector.get(CurrencyService).allCached();
        const currency   = currencies.all().find(_currency => _currency.code.toUpperCase() === currencyCode.toUpperCase());

        // see https://github.com/Torann/laravel-currency/blob/master/src/Currency.php
        const format     = currency.format;
        const seperators = format.match(/[\s\',.!]/g);
        const thousand   = seperators ? seperators[0] : null;
        const decimal    = seperators ? seperators[1] : null;
        const valFormat  = format.match(/([0-9].*|)[0-9]/)[0] || null;
        const decimals   = valFormat.substr((valFormat.indexOf(decimal) + 1)).length;

        currency['settings'] = {thousand, decimal, decimals};

        return currency;
    }

    /**
     * Convert from default rate to another
     *
     * @param {number | string} value
     * @param {string} toCurrency
     * @returns {any}
     */
    public convert(value: number | string, toCurrency: string | CurrencyModel, fromCurrency: string | CurrencyModel = null, toCurrencyRate = null, fromCurrencyRate = null) {
        if (!toCurrency) {
            return value;
        }

        if (typeof toCurrency === 'string') {
            toCurrency = this.getCurrency(toCurrency || this.defaultCurrency.code);
        }

        if (!fromCurrency || !(fromCurrency instanceof CurrencyModel)) {
            if (typeof fromCurrency === 'string') {
                fromCurrency = this.getCurrency(fromCurrency);
            } else {
                fromCurrency = this.defaultCurrency;
            }
        }

        if (toCurrencyRate === null) {
            toCurrencyRate = toCurrency.exchange_rate;
        }

        if (fromCurrencyRate === null) {
            fromCurrencyRate = fromCurrency.exchange_rate;
        }

        value            = Number.isNaN(Number(value)) ? 0 : Number(value);
        toCurrencyRate   = Number.isNaN(Number(toCurrencyRate)) ? 0 : Number(toCurrencyRate);
        fromCurrencyRate = Number.isNaN(Number(fromCurrencyRate)) ? 0 : Number(fromCurrencyRate);

        value = value && toCurrencyRate && fromCurrencyRate
            ? value / fromCurrencyRate * toCurrencyRate
            : value;

        return Number(value).toFixed(3);
    }

    /**
     * PHP alike number_format
     *
     * @param number
     * @param decimals
     * @param dec_point
     * @param thousands_sep
     * @returns {string}
     */
    protected number_format(number, decimals, dec_point, thousands_sep): string {
        // Strip all characters but numerical ones.
        number         = (number + '').replace(/[^0-9+\-Ee.]/g, '');
        let s: any     = '';
        const n        = !isFinite(+number) ? 0 : +number,
            prec       = !isFinite(+decimals) ? 0 : Math.abs(decimals),
            sep        = (typeof thousands_sep === 'undefined') ? ',' : thousands_sep,
            dec        = (typeof dec_point === 'undefined') ? '.' : dec_point,
            toFixedFix = function (_n, _prec) {
                const k = Math.pow(10, _prec);
                return '' + Math.round(_n * k) / k;
            };
        // Fix for IE parseFloat(0.55).toFixed(0) = 0;
        s              = (prec ? toFixedFix(n, prec) : '' + Math.round(n)).split('.');
        if (s[0].length > 3) {
            s[0] = s[0].replace(/\B(?=(?:\d{3})+(?!\d))/g, sep);
        }
        if ((s[1] || '').length < prec) {
            s[1] = s[1] || '';
            s[1] += new Array(prec - s[1].length + 1).join('0');
        }
        return s.join(dec);
    }
}



// WEBPACK FOOTER //
// ./libs/chassis-core/src/app/currency/pipes/currency.pipe.ts